import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { useMediaQuery, SelectChangeEvent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "react-query";
import getParams from "../../hooks/useParams";
import { useLocation, useNavigate } from "react-router-dom";
import useSessionState from "../../hooks/useSessionState";
import { useStore } from "../../store/store";
import {
  searchAllQuotes,
  getAllQuotes,
  postHideTips,
} from "../../api/DzoneApi";
import { checkVersion } from "../../api/Authentication";
import { getServiceNumber } from "../../api/ServiceApi";
import { roleBasedTabs, isMobile } from "./dealerZone.biz";
import { RepairState, TableState } from "./DealerZone";

function useDZoneSelector() {
  const resetQuote = useStore((state) => state.resetQuote);

  const role = JSON.parse(sessionStorage.getItem("role") || JSON.stringify(""));
  const email = JSON.parse(
    sessionStorage.getItem("email") || JSON.stringify(""),
  );

  const logo = JSON.parse(
    sessionStorage.getItem("dealerLogo") || JSON.stringify(""),
  );

  useEffect(() => {
    localStorage.clear();
    resetQuote();

    if (role === "Production") {
      setOrdersTable({
        page: 0,
        rowsPerPage: 15,
        totalResults: 0,
        searchTerm: "",
        filterBy: "salesOrder",
      });
      setShowTab("Orders");
    }

    (async function () {
      await checkVersion();
    })();

    if (isMobile()) return;

    const showTips = JSON.parse(
      sessionStorage.getItem("showTips") || JSON.stringify(""),
    );

    if (showTips) {
      handleOpenTips();
      sessionStorage.setItem("showTips", JSON.stringify(false));
    }
  }, []);

  const location = useLocation();
  const id = getParams(location.pathname);

  const navigate = useNavigate();
  const theme = useTheme();
  const mediaLg = useMediaQuery(theme.breakpoints.up("md"));

  const [mobileOpen, setMobileOpen] = useState(false);

  const [showTab, setShowTab] = useSessionState("showTab", "Home");

  const [quotesTable, setQuotesTable] = useSessionState("quotesTable", {
    page: 0,
    rowsPerPage: 15,
    totalResults: 0,
    searchTerm: "",
    filterBy: "quoteNumber",
  });

  const [ordersTable, setOrdersTable] = useSessionState("ordersTable", {
    page: 0,
    rowsPerPage: 15,
    totalResults: 0,
    searchTerm: "",
    filterBy: "quoteNumber",
  });

  const [isSearchQuotes, setIsSearchQ] = useSessionState(
    "isSearchQuotes",
    false,
  );

  const [isSearchOrders, setIsSearchO] = useSessionState(
    "isSearchOrders",
    false,
  );

  const [reSearchQuotes, setReSearchQ] = useState(false);
  const [reSearchOrders, setReSearchO] = useState(false);

  const [openTableSwitchModal, setOpenTableSwitchModal] = useState(false);

  const handleCloseSwitchTableModal = () => setOpenTableSwitchModal(false);

  const handleOpenTableSwitchModal = () => setOpenTableSwitchModal(true);

  const handleTableSwitch = (status: string) => {
    handleCloseSwitchTableModal();
    if (status === "Quotes") {
      setQuotesTable((prev) => ({
        ...prev,
        searchTerm: ordersTable.searchTerm,
        filterBy: ordersTable.filterBy,
        page: 0,
      }));
      setOrdersTable((prev) => ({ ...prev, searchTerm: "", page: 0 }));
      setIsSearchQ(true);
      setIsSearchO(false);
      setShowArchive("Quotes");
      setReSearchQ((prev) => !prev);
    } else if (status === "Orders") {
      setOrdersTable((prev) => ({
        ...prev,
        searchTerm: quotesTable.searchTerm,
        filterBy: quotesTable.filterBy,
        page: 0,
      }));
      setQuotesTable((prev) => ({ ...prev, searchTerm: "", page: 0 }));
      setIsSearchO(true);
      setIsSearchQ(false);
      setReSearchO((prev) => !prev);
    }
    setShowTab(status);
  };

  const [showArchive, setShowArchive] = useSessionState(
    "showArchive",
    "Quotes",
  );

  const handleArchive = (event: SelectChangeEvent<string>) => {
    setShowArchive(event.target.value);
  };

  const searchAll = async (
    status: string,
    table: TableState,
    setTotal: Dispatch<SetStateAction<TableState>>,
    archive?: boolean,
  ) => {
    const res = await searchAllQuotes({
      searchTerm: table.searchTerm.toLowerCase(),
      status,
      searchBy: table.filterBy,
      pageNumber: table.page,
      rowsPerPage: table.rowsPerPage,
      showArchive: archive || false,
    });

    if (
      res.data &&
      typeof res.data === "string" &&
      res.data.startsWith("Quotes Found In")
    ) {
      //7/17/2026 - No longer showing this modal due to quotes and orders having the same number and just returning quote/order not found
      //handleOpenTableSwitchModal();
      return "No Quotes Found";
    } else if (res.data !== "No Quotes Found") {
      setTotal((prev) => ({
        ...prev,
        totalResults: res.data.totalResults,
      }));
    }

    return res.data;
  };

  const {
    isLoading: isLoadingQuotes,
    error: quotesError,
    data: quotesData,
    isFetching: isFetchingQuotes,
  } = useQuery(
    [
      "Quotes",
      isSearchQuotes,
      quotesTable.page,
      quotesTable.rowsPerPage,
      reSearchQuotes,
      showArchive,
    ],
    async () => {
      if (role === "Production") return;

      if (!isSearchQuotes || !quotesTable.searchTerm) {
        const response = await getAllQuotes({
          pageNumber: quotesTable.page,
          rowsPerPage: quotesTable.rowsPerPage,
          status: "Quotes",
          showArchive,
        });

        setQuotesTable((prev: TableState) => ({
          ...prev,
          totalResults: response.data.totalResults,
        }));
        return response.data;
      } else {
        const searchData = await searchAll(
          "Quotes",
          quotesTable,
          setQuotesTable,
          showArchive,
        );
        return searchData;
      }
    },
    {
      retry: 2,
    },
  );

  const { isLoading, error, data, isFetching } = useQuery(
    [
      "Orders",
      isSearchOrders,
      ordersTable.page,
      ordersTable.rowsPerPage,
      reSearchOrders,
    ],
    async () => {
      if (!isSearchOrders || !ordersTable.searchTerm) {
        const response = await getAllQuotes({
          pageNumber: ordersTable.page,
          rowsPerPage: ordersTable.rowsPerPage,
          status: "Orders",
        });

        setOrdersTable((prev: TableState) => ({
          ...prev,
          totalResults: response.data.totalResults,
        }));
        return response.data;
      } else {
        const searchData = await searchAll(
          "Orders",
          ordersTable,
          setOrdersTable,
        );
        return searchData;
      }
    },
    {
      retry: 2,
    },
  );

  const handleTabChange = (tab: string) => {
    if (tab === showTab) return;

    if (showTab === "Service Repair" && snNumber) {
      return handleOpen(tab);
    }
    setShowTab(tab);
  };

  const changeTab = () => {
    setSnNumber(0);
    setOpen(false);
    repair.status && setRepair({ status: "", id: "" });
    setShowTab(tabToSwitch);
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleNewQuote = () =>
    navigate(`/form/${id}`, {
      state: { isQuoteCreated: false },
    });

  const [snNumber, setSnNumber] = useState(0);

  const getSN = async () => {
    const serviceNumber = await getServiceNumber();
    serviceNumber.data.snNumber && setSnNumber(serviceNumber.data.snNumber);
  };

  const [repair, setRepair] = useState<RepairState>({ status: "", id: "" });

  const [tabToSwitch, setTTS] = useState("");

  const [open, setOpen] = useState(false);
  const handleOpen = (tab: string) => {
    setTTS(tab);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const [openTips, setOpenTips] = useState(false);
  const handleOpenTips = () => setOpenTips(true);

  const handleCloseTips = async (dontShow?: boolean) => {
    if (dontShow) {
      const response = await postHideTips();
      response.data === "AG Tips Hidden" && setOpenTips(false);
      return;
    }
    setOpenTips(false);
  };

  const showInsights =
    role === "Admin" ||
    role === "Outside Sales" ||
    email === "laura@agmillworks.com"  ||
    email === "jarrod@agmillworks.com"
      ? true
      : false;

  const h2Titles = ["Quotes", "Orders", "Glass Details", "Customer List"];

  const findColor = (text: string, color: string) =>
    showTab === text ? color : "";

  const tabOptions = roleBasedTabs(role, showInsights);

  return {
    tabOptions,
    findColor,
    h2Titles,
    handleCloseTips,
    openTips,
    handleClose,
    open,
    getSN,
    handleNewQuote,
    handleDrawerToggle,
    changeTab,
    handleTabChange,
    isLoading,
    error,
    data,
    isFetching,
    isLoadingQuotes,
    quotesError,
    quotesData,
    isFetchingQuotes,
    handleArchive,
    showArchive,
    setReSearchO,
    setReSearchQ,
    setIsSearchO,
    isSearchOrders,
    setIsSearchQ,
    isSearchQuotes,
    ordersTable,
    quotesTable,
    setQuotesTable,
    setOrdersTable,
    showTab,
    setShowTab,
    mobileOpen,
    mediaLg,
    theme,
    id,
    logo,
    role,
    snNumber,
    setSnNumber,
    repair,
    setRepair,
    openTableSwitchModal,
    handleTableSwitch,
    handleOpenTableSwitchModal,
    handleCloseSwitchTableModal,
  };
}

export default useDZoneSelector;
